import { useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  TrendingUp, TrendingDown, Info, Users, FileText, Activity, MessageCircle, Shield,
  Clock, AlertTriangle, ArrowUpDown , Zap, Target
} from "lucide-react";
import { selectSentimentByTicker, selectSentimentLoading } from "../../../redux/sentimentAggregator/sentimentAggSlice";
import { fetchStockSentimentAggsThunk } from "../../../redux/sentimentAggregator/sentimentAggActionThunks";
import NewsSentimentBar from "../../../components/sentimentNews/sentimentNewsAggregator";
import InfoTooltip from "../../../components/ui/InfoTooltip";


// Backend sentiment values are finer-grained than the 3 buckets above (e.g. "weak_neutral",
// "weak_bullish") -- these were rendering as raw snake_case in the pill (see screenshot report:
// "weak_neutral" overflowing its card). Prettify for display without changing the underlying value.
const formatSentimentLabel = (sentiment, t) => {
  if (!sentiment) return t('stocks.sentimentWords.neutral');
  return sentiment
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const sentimentColors = {
  bullish: {
    color: "text-emerald-700 dark:text-emerald-400",
    bg: "bg-emerald-50 border-emerald-300 dark:bg-emerald-900/30 dark:border-emerald-600/60",
    icon: TrendingUp,
  },
  bearish: {
    color: "text-red-700 dark:text-red-400",
    bg: "bg-red-50 border-red-300 dark:bg-red-900/30 dark:border-red-600/60",
    icon: TrendingDown,
  },
  neutral: {
    color: "text-gray-600 dark:text-gray-400",
    bg: "bg-gray-100 border-gray-300 dark:bg-gray-800/60 dark:border-gray-600/60",
    icon: ArrowUpDown,
  },
};

const getSectionConfig = (t) => ({
  retail: {
    label: t('stocks.sentimentAgg.sections.retail.label'),
    icon: Users,
    color: "text-blue-600 dark:text-blue-400",
    weight: 0.2,
    description: t('stocks.sentimentAgg.sections.retail.description')
  },
  credible: {
    label: t('stocks.sentimentAgg.sections.credible.label'),
    icon: Shield,
    color: "text-purple-600 dark:text-purple-400",
    weight: 0.5,
    description: t('stocks.sentimentAgg.sections.credible.description')
  },
  noise: {
    label: t('stocks.sentimentAgg.sections.noise.label'),
    icon: Activity,
    color: "text-yellow-700 dark:text-yellow-400",
    weight: 0.05,
    description: t('stocks.sentimentAgg.sections.noise.description')
  },
  groups_membership: {
    label: t('stocks.sentimentAgg.sections.groupsMembership.label'),
    icon: Users,
    color: "text-cyan-700 dark:text-cyan-400",
    weight: 0.15,
    description: t('stocks.sentimentAgg.sections.groupsMembership.description')
  },
  groups_posts: {
    label: t('stocks.sentimentAgg.sections.groupsPosts.label'),
    icon: MessageCircle,
    color: "text-pink-600 dark:text-pink-400",
    weight: 0.1,
    description: t('stocks.sentimentAgg.sections.groupsPosts.description')
  },
  news: {
    label: t('stocks.sentimentAgg.sections.news.label'),
    icon: FileText,
    color: "text-blue-600 dark:text-blue-400",
    weight: 0.2,
    description: t('stocks.sentimentAgg.sections.news.description')
  },
});

const getTimeframeOptions = (t) => [
  { value: "7d", label: t('stocks.sentimentAgg.timeframes.sevenDays.label'), description: t('stocks.sentimentAgg.timeframes.sevenDays.description') },
  { value: "24h", label: t('stocks.sentimentAgg.timeframes.twentyFourHours.label'), description: t('stocks.sentimentAgg.timeframes.twentyFourHours.description') },
  { value: "1m", label: t('stocks.sentimentAgg.timeframes.oneMonth.label'), description: t('stocks.sentimentAgg.timeframes.oneMonth.description') },
  { value: "3m", label: t('stocks.sentimentAgg.timeframes.threeMonths.label'), description: t('stocks.sentimentAgg.timeframes.threeMonths.description') },
  { value: "1y", label: t('stocks.sentimentAgg.timeframes.oneYear.label'), description: t('stocks.sentimentAgg.timeframes.oneYear.description') },
  { value: "all", label: t('stocks.sentimentAgg.timeframes.allTime.label'), description: t('stocks.sentimentAgg.timeframes.allTime.description') },
];

const ConfidenceMeter = ({ score, sampleSize, className = "" }) => {
  const { t } = useTranslation();
  const getConfidenceLevel = (size) => {
    if (size >= 100) return { level: t('stocks.sentimentAgg.confidence.high'), color: "text-emerald-700 dark:text-emerald-400", bg: "bg-emerald-500" };
    if (size >= 25) return { level: t('stocks.sentimentAgg.confidence.medium'), color: "text-yellow-700 dark:text-yellow-400", bg: "bg-yellow-500" };
    return { level: t('stocks.sentimentAgg.confidence.low'), color: "text-red-600 dark:text-red-400", bg: "bg-red-500" };
  };

  const confidence = getConfidenceLevel(sampleSize);

  return (
      <div className={`flex items-center gap-1 ${className}`}>
        <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
          <div
            className={`h-full rounded-full transition-all ${confidence.bg}`}
            style={{ width: `${Math.min(score * 100, 100)}%` }}
          />
        </div>
        <span className={`text-xs ${confidence.color}`}>{confidence.level}</span>
      </div>
  );
};

const DivergenceAlert = ({ sections }) => {
  const { t } = useTranslation();
  const credible = sections.find(s => s.key === 'credible');
  const retail = sections.find(s => s.key === 'retail');
  const noise = sections.find(s => s.key === 'noise');

  if (!credible?.data?.votes || !retail?.data?.votes) return null;

  const credibleBullish = credible.data.votes.bullish_ratio > 0.6;
  const credibleBearish = credible.data.votes.bullish_ratio < 0.4;
  const retailBullish = retail.data.votes.bullish_ratio > 0.6;
  const retailBearish = retail.data.votes.bullish_ratio < 0.4;

  const divergence = (credibleBullish && retailBearish) || (credibleBearish && retailBullish);

  if (!divergence) return null;

  // Deliberately descriptive, not advisory: this reports what the two groups' votes show,
  // not what to do about it. Earlier copy ("potential alpha opportunity" / "exercise caution")
  // read as a trading recommendation, which cuts against the platform's own "not investment
  // advice" disclaimer (Technical_Docs-adjacent: LegalPage.jsx's DisclaimerPage) -- softened
  // to state the observed split without directional guidance.
  const alertText = credibleBullish && retailBearish
    ? t('stocks.sentimentAgg.divergence.credibleBullishRetailBearish')
    : t('stocks.sentimentAgg.divergence.credibleBearishRetailBullish');

  return (
    <div className="bg-orange-50 border border-orange-300 dark:bg-orange-900/30 dark:border-orange-600/60 rounded-lg px-3 py-2 flex items-center gap-2">
      <Zap className="w-4 h-4 text-orange-700 dark:text-orange-400" />
      <span className="text-xs text-orange-700 dark:text-orange-400 font-medium">{alertText}</span>
    </div>
  );
};

const SentimentAggChip = ({ ticker, defaultTimeframe = "7d" }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedTimeframe, setSelectedTimeframe] = useState(defaultTimeframe);

  const sectionConfig = useMemo(() => getSectionConfig(t), [t]);
  const timeframeOptions = useMemo(() => getTimeframeOptions(t), [t]);

  const sentimentData = useSelector((state) =>
    selectSentimentByTicker(state, ticker, selectedTimeframe)
  );
  const sentimentLoading = useSelector((state) =>
    selectSentimentLoading(state, ticker, selectedTimeframe)
  );

  // Fetch data when ticker or timeframe changes
  useEffect(() => {
    if (ticker) {
      dispatch(fetchStockSentimentAggsThunk({ ticker, timeframe: selectedTimeframe }));
    }
  }, [ticker, selectedTimeframe, dispatch]);

  const handleTimeframeChange = (newTimeframe) => {
    setSelectedTimeframe(newTimeframe);
  };

  const analysis = useMemo(() => {
    if (sentimentLoading) return { loading: true };
    if (!sentimentData) return null;

    // Calculate weighted sentiment score
    let weightedBullishScore = 0;
    let totalWeight = 0;
    let totalVotes = 0;
    let totalBullish = 0;
    let totalBearish = 0;
    let totalArticles = 0;

    const sections = Object.entries(sectionConfig)
      .map(([key, config]) => {
        const data = sentimentData[key];
        // Show all sections that have data, even if empty
        if (!data) {
          return {
            key,
            ...config,
            data: null,
            sentiment: 'neutral',
            sentimentConfig: sentimentColors.neutral,
            bullishRatio: 0,
            sampleSize: 0,
          };
        }
        
        const sectionSentiment = data.combined_sentiment || 'neutral';
        // Backend values are finer-grained than the 3 buckets below (e.g. "weak_bullish"),
        // so match on the base word rather than requiring an exact key -- otherwise every
        // "weak_*" value silently fell back to neutral (gray) styling regardless of its
        // actual direction.
        const sentimentConfig =
          sentimentColors[sectionSentiment] ||
          (sectionSentiment.includes('bullish') && sentimentColors.bullish) ||
          (sectionSentiment.includes('bearish') && sentimentColors.bearish) ||
          sentimentColors.neutral;
        
        if (data.votes) {
          const bullishRatio = data.votes.bullish_ratio || 0;
          weightedBullishScore += bullishRatio * config.weight;
          totalWeight += config.weight;
          totalVotes += data.votes.total_votes || 0;
          totalBullish += data.votes.bullish_count || 0;
          totalBearish += data.votes.bearish_count || 0;
        }
        
        if (data.news) {
          totalArticles += data.news.articles_analyzed || 0;
        }
        
        return {
          key,
          ...config,
          data,
          sentiment: sectionSentiment,
          sentimentConfig,
          bullishRatio: data.votes?.bullish_ratio || 0,
          sampleSize: data.votes?.total_votes || 0,
        };
      });

    // Calculate overall weighted sentiment
    const weightedSentiment = totalWeight > 0 ? weightedBullishScore / totalWeight : 0;
    let overallSentiment = 'neutral';
    if (weightedSentiment > 0.6) overallSentiment = 'bullish';
    else if (weightedSentiment < 0.4) overallSentiment = 'bearish';
    
    const sentimentConfig = sentimentColors[overallSentiment];
    
    // Calculate confidence based on credible sample size
    const credibleSection = sections.find(s => s.key === 'credible');
    const confidenceScore = credibleSection ? Math.min(credibleSection.sampleSize / 100, 1) : 0;

    return {
      overallSentiment,
      sentimentConfig,
      weightedSentiment,
      confidenceScore,
      totalVotes,
      totalBullish,
      totalBearish,
      totalArticles,
      sections,
      lastUpdated: sentimentData.last_updated,
    };
  }, [sentimentData, sentimentLoading, sectionConfig]);

  if (analysis?.loading) {
    return (
      <div className="space-y-4">
        {/* Timeframe Selector - Show even when loading */}
        <div className="flex items-center gap-2 flex-wrap">
          <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">{t('stocks.sentimentAgg.timeframeLabel')}</span>
          {timeframeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleTimeframeChange(option.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                selectedTimeframe === option.value
                  ? "bg-blue-600 border-blue-500 text-white"
                  : "bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200 hover:text-gray-900 dark:bg-slate-800/60 dark:border-slate-600 dark:text-gray-400 dark:hover:bg-slate-700/60 dark:hover:text-white"
              }`}
              title={option.description}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="bg-gray-100 border border-gray-300 dark:bg-gray-800/60 dark:border-gray-600 rounded-lg px-4 py-2">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-blue-400/60 animate-pulse"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('stocks.sentimentAgg.loadingAnalysis')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="space-y-4">
        {/* Timeframe Selector */}
        <div className="flex items-center gap-2 flex-wrap">
          <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">{t('stocks.sentimentAgg.timeframeLabel')}</span>
          {timeframeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleTimeframeChange(option.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                selectedTimeframe === option.value
                  ? "bg-blue-600 border-blue-500 text-white"
                  : "bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200 hover:text-gray-900 dark:bg-slate-800/60 dark:border-slate-600 dark:text-gray-400 dark:hover:bg-slate-700/60 dark:hover:text-white"
              }`}
              title={option.description}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="bg-gray-100 border border-gray-300 dark:bg-gray-800/60 dark:border-gray-600 rounded-lg px-4 py-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">{t('stocks.sentimentAgg.noDataForTicker', { ticker, timeframe: selectedTimeframe })}</span>
        </div>
      </div>
    );
  }

  const { sentimentConfig } = analysis;
  const { icon: MainIcon } = sentimentConfig;
  const bullishRatio = analysis.totalVotes > 0 ? (analysis.totalBullish / analysis.totalVotes * 100) : 0;
  const bearishRatio = analysis.totalVotes > 0 ? (analysis.totalBearish / analysis.totalVotes * 100) : 0;

return (
  <div className="space-y-4">
    {/* Timeframe Selector */}
    <div className="flex items-center gap-2 flex-wrap">
      <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
      <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">{t('stocks.sentimentAgg.timeframeLabel')}</span>
      {timeframeOptions.map((option) => (
        <button
          key={option.value}
          onClick={() => handleTimeframeChange(option.value)}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
            selectedTimeframe === option.value
              ? "bg-blue-600 border-blue-500 text-white shadow-lg"
              : "bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200 hover:text-gray-900 hover:border-gray-400 dark:bg-slate-800/60 dark:border-slate-600 dark:text-gray-400 dark:hover:bg-slate-700/60 dark:hover:text-white dark:hover:border-slate-500"
          }`}
          title={option.description}
        >
          {option.label}
        </button>
      ))}
    </div>

    {/* Enhanced Main Sentiment Overview */}
    <div className="flex items-center gap-4 flex-wrap">
      {/* Weighted Overall Sentiment */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${sentimentConfig.bg} ${sentimentConfig.color} relative`}>
        <MainIcon className="w-5 h-5" />
        <div>
          <div className="font-bold text-sm flex items-center gap-2">
            {t('stocks.sentimentAgg.overallSentiment', { sentiment: analysis.overallSentiment.charAt(0).toUpperCase() + analysis.overallSentiment.slice(1) })}
            <InfoTooltip text={t('stocks.sentimentAgg.weightedTooltip')} />
          </div>
          <div className="text-xs opacity-80 flex items-center gap-2">
            {timeframeOptions.find(tf => tf.value === selectedTimeframe)?.description || selectedTimeframe}
            <span className="text-xs">({(analysis.weightedSentiment * 100).toFixed(0)}% weighted bullish)</span>
          </div>
        </div>
      </div>

      {/* Confidence Meter */}
      <div className="bg-gray-100 border border-gray-300 dark:bg-slate-800/60 dark:border-slate-600 rounded-lg px-4 py-3">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          <div>
            <div className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
              {t('stocks.sentimentAgg.confidenceScoreLabel')}
              <InfoTooltip text={t('stocks.sentimentAgg.confidenceScoreTooltip')} />
            </div>
            <ConfidenceMeter
              score={analysis.confidenceScore}
              sampleSize={analysis.sections.find(s => s.key === 'credible')?.sampleSize || 0}
            />
          </div>
        </div>
      </div>

      {/* Total Votes Summary */}
      {analysis.totalVotes > 0 && (
        <div className="bg-gray-100 border border-gray-300 dark:bg-slate-800/60 dark:border-slate-600 rounded-lg px-4 py-3">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <div>
              <div className="text-sm font-bold text-gray-900 dark:text-white">{t('stocks.sentimentAgg.totalVotes', { count: analysis.totalVotes.toLocaleString() })}</div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-emerald-700 dark:text-emerald-400 font-medium">
                  {t('stocks.sentimentAgg.bullishPct', { pct: bullishRatio.toFixed(1), count: analysis.totalBullish })}
                </span>
                <span className="text-red-700 dark:text-red-400 font-medium">
                  {t('stocks.sentimentAgg.bearishPct', { pct: bearishRatio.toFixed(1), count: analysis.totalBearish })}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Articles Analyzed */}
      {analysis.totalArticles > 0 && (
        <div className="bg-gray-100 border border-gray-300 dark:bg-slate-800/60 dark:border-slate-600 rounded-lg px-4 py-3">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <div>
              <div className="text-sm font-bold text-gray-900 dark:text-white">{t('stocks.sentimentAgg.articlesCount', { count: analysis.totalArticles })}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('stocks.sentimentAgg.newsAnalysis')}</div>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Divergence Alert */}
    <DivergenceAlert sections={analysis.sections} />

    {/* Enhanced Detailed Breakdown by Source - Show ALL sections */}
    {/* Capped at 3 columns permanently (was 2xl:grid-cols-5) -- there are
        exactly 6 sections, so 5 columns always orphaned the 6th card alone
        on its own row at ultra-wide viewports (reported as looking broken/
        outside its allotted area), on top of the original overflow problem
        (5 columns left too little width per card, spilling the sentiment
        pill into the neighboring card). 3 always divides 6 evenly. */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {analysis.sections.map((section) => {
        const SectionIcon = section.icon;
        const isHighWeight = section.weight >= 0.3;
        const hasData = section.data && section.data.votes && section.data.votes.total_votes > 0;

        return (
          <div
            key={section.key}
            className={`bg-gray-50 hover:bg-gray-100 dark:bg-slate-800/80 dark:hover:bg-slate-700/60 border rounded-lg p-3 transition-colors cursor-help min-w-0 ${
              isHighWeight ? 'border-purple-400/60 ring-1 ring-purple-400/20 dark:border-purple-500/40 dark:ring-purple-500/20' : 'border-gray-200 dark:border-slate-600'
            } ${!hasData ? 'opacity-60' : ''}`}
          >
            {/* Section Header */}
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <SectionIcon className={`w-4 h-4 flex-shrink-0 ${section.color}`} />
                <span className="text-sm font-bold text-gray-900 dark:text-white truncate">{section.label}</span>
                {isHighWeight && (
                  <span className="flex items-center gap-0.5 flex-shrink-0">
                    <Target className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                    <InfoTooltip text={t('stocks.sentimentAgg.highImportanceWeight')} />
                  </span>
                )}
              </div>
              <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full flex-shrink-0 whitespace-nowrap ${section.sentimentConfig.bg} ${section.sentimentConfig.color}`}>
                <section.sentimentConfig.icon className="w-3 h-3 flex-shrink-0" />
                {formatSentimentLabel(section.sentiment, t)}
              </div>
            </div>

            {/* Vote Details with Weight Indicator */}
            <div className="space-y-2">
              {hasData ? (
                <>
                  <div className="flex justify-between text-xs">
                    <span className="text-emerald-700 dark:text-emerald-400 font-medium">
                      {t('stocks.sentimentAgg.bullishCount', { count: section.data.votes.bullish_count })}
                    </span>
                    <span className="text-red-700 dark:text-red-400 font-medium">
                      {t('stocks.sentimentAgg.bearishCount', { count: section.data.votes.bearish_count })}
                    </span>
                  </div>

                  <div className="text-xs text-center text-gray-500 dark:text-gray-400">
                    {t('stocks.sentimentAgg.totalLabel')} <span className="text-gray-900 dark:text-white font-bold">{section.data.votes.total_votes}</span>
                    <span className="ml-2 text-purple-600 dark:text-purple-400">
                      {t('stocks.sentimentAgg.weightSuffix', { pct: (section.weight * 100).toFixed(0) })}
                    </span>
                  </div>

                  {/* Enhanced Progress Bar */}
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div className="h-full flex">
                      <div
                        className={`transition-all duration-300 ${
                          section.data.votes.bullish_ratio > 0.7 ? 'bg-emerald-300' : 'bg-emerald-400'
                        }`}
                        style={{ width: `${section.data.votes.bullish_ratio * 100}%` }}
                      ></div>
                      <div
                        className={`transition-all duration-300 ${
                          section.data.votes.bearish_ratio > 0.7 ? 'bg-red-300' : 'bg-red-400'
                        }`}
                        style={{ width: `${section.data.votes.bearish_ratio * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Confidence indicator */}
                  <ConfidenceMeter score={Math.min(section.data.votes.total_votes / 50, 1)} sampleSize={section.data.votes.total_votes} />

                  {/* ✅ News Sentiment Bar */}
                  {section.data.news && <NewsSentimentBar news={section.data.news} />}
                </>
              ) : (
                <>
                  {/* 2026-09-11: this said "No data available" even when
                      section.data.news existed and rendered right below it
                      with real numbers -- reading as directly contradictory.
                      Distinguish "no votes" (there's other data) from
                      "nothing at all" (truly empty section). */}
                  <div className="text-xs text-center text-gray-500 dark:text-gray-400">
                    {t(section.data?.news ? 'stocks.sentimentAgg.noVotesYet' : 'stocks.sentimentAgg.noDataAvailable')}
                    <span className="ml-2 text-purple-600 dark:text-purple-400">
                      {t('stocks.sentimentAgg.weightSuffix', { pct: (section.weight * 100).toFixed(0) })}
                    </span>
                  </div>

                  {/* Empty Progress Bar */}
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div className="h-full bg-gray-300 dark:bg-gray-600"></div>
                  </div>

                  {/* No Confidence indicator */}
                  <ConfidenceMeter score={0} sampleSize={0} />

                  {/* ✅ News Sentiment Bar (even if no votes) */}
                  {section.data?.news && <NewsSentimentBar news={section.data.news} />}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>

    {/* Last Updated */}
    {analysis.lastUpdated && (
      <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 dark:bg-slate-800/40 dark:border-slate-700 rounded-lg px-3 py-2">
        <div>{t('stocks.sentimentAgg.updatedLabel')} {new Date(analysis.lastUpdated).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })}</div>
      </div>
    )}
  </div>
);

};

export default SentimentAggChip;